
import { AuthContext } from "../context/AuthContext";
import React, { useState, useContext, useRef } from "react";



function BirdIdentifier() {
  const { token } = useContext(AuthContext);
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [followUp, setFollowUp] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [refined, setRefined] = useState(null);
  const [readAloud, setReadAloud] = useState(false);
  const [showRefineForm, setShowRefineForm] = useState(false);

  
  const synthRef = useRef(window.speechSynthesis);

  // speech function
  const speak = (text) => {
    if (!readAloud || !synthRef.current) return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-GB";
    utterance.rate = 1;
    utterance.pitch = 1;
    synthRef.current.cancel();
    synthRef.current.speak(utterance);
  };



  const saveToJournal = (entry) => {
    const journal = JSON.parse(localStorage.getItem("raptorid-journal")) || [];
    journal.push(entry);
    localStorage.setItem("raptorid-journal", JSON.stringify(journal));
    alert("Saved to journal!");
  };

  const handleVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Your browser doesn't support voice recognition.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-GB";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setDescription(transcript);
    };

    recognition.onerror = (event) => {
      alert("Voice input error: " + event.error);
    };

    recognition.start();
  };

  const handleGetLocation = async () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`);
          const data = await res.json();
          setLocation(data.display_name || `Lat: ${lat}, Lon: ${lon}`);
        } catch (err) {
          console.error("Geolocation error:", err);
          setLocation(`Lat: ${lat}, Lon: ${lon}`);
        }
      },
      () => {
        alert("Unable to retrieve your location.");
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    setRefined(null);
    setAnswers([]);
    setFollowUp(null);


    if (!token) {
      setError("You must be logged in.");
      return;
    }
    if (!description && !imageFile) {
      setError("Please provide a description or image.");
      return;
    }

    try {
      setLoading(true);

      let azureTags = [];
      let azureDescription = "";
      let azureObjects = [];
      const photoProvided = Boolean(imageFile);

      if (photoProvided) {
        const arrayBuffer = await imageFile.arrayBuffer();

        const azureRes = await fetch("https://reactraptorid.onrender.com/analyze", {
          method: "POST",
          headers: {
            "Content-Type": "application/octet-stream",
            Authorization: "Bearer " + token,
          },
          body: arrayBuffer,
        });

        if (!azureRes.ok) {
          const msg = await azureRes.text();
          throw new Error("Azure error: " + msg);
        }

        const azureData = await azureRes.json();
        azureDescription = azureData.description?.captions?.[0]?.text || "";
        azureTags = azureData.description?.tags || [];
        azureObjects = azureData.objects?.map((obj) => obj.object) || [];
      }

      const idRes = await fetch("https://reactraptorid.onrender.com/identify", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          description,
          location,
          photoProvided,
          azureDescription,
          azureTags,
          azureObjects,
        }),
      });

      if (!idRes.ok) {
        const msg = await idRes.text();
        throw new Error("ID error: " + msg);
      }

      const data = await idRes.json();
      setResult(data);
      setFollowUp(data.followUp || null);
      setAnswers([]);

      speak(`Species: ${data.bird}. Why: ${data.why}. Confidence: ${data.confidence}`);
    } catch (err) {
      console.error(err);
      setError(err.message || "something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefine = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch("https://reactraptorid.onrender.com/refine", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          originalBird: result.bird,
          followUpAnswers: answers,
          description,
          location,
        }),
      });

      if (!res.ok) {
        const msg = await res.text();
        throw new Error("Refine error: " + msg);
      }

      const refinedResult = await res.json();
      
      setRefined(refinedResult);
      setShowRefineForm(false);
      setFollowUp(null);
      speak(`Refined result: Species: ${refinedResult.bird}. Why: ${refinedResult.why}. Confidence: ${refinedResult.confidence}`);

    } catch (err) {
      console.error(err);
      setError("Refinement failed: " + err.message);
    }
  };

  return (  
<div className="container">
  <h2 className="text-center">Identify a Bird of Prey</h2>

  <form className="bird-form" onSubmit={handleSubmit}>
    <label>Upload a Photo:</label>
    <input
      type="file"
      accept="image/*"
      onChange={(e) => setImageFile(e.target.files[0])}
    />

    <label>Or describe the bird:</label>
    <input
      type="text"
      placeholder="e.g., large brown bird with forked tail"
      value={description}
      onChange={(e) => setDescription(e.target.value)}
    />
    <button type="button" className="voice-button" onClick={handleVoiceInput}>
      Voice description 🎤
    </button>
    
    <br />

    <label>Location (optional):</label>
    <input
      type="text"
      placeholder="e.g., Lake District"
      value={location}
      onChange={(e) => setLocation(e.target.value)}
    />
    <button type="button" onClick={handleGetLocation}>
      Use Current Location 📍
    </button>

    <div className="read-aloud-row">
      <span role="img" aria-label="speaker" className="speaker-icon">🔊</span>
      <label>
        Read results aloud
        <input
          type="checkbox"
          checked={readAloud}
          onChange={() => setReadAloud(!readAloud)}
        />
      </label>
    </div>

    <button type="submit" disabled={loading}>
      {loading ? "Identifying..." : "Check"}
    </button>
  </form>

  {error && <p style={{ color: "red" }}>{error}</p>}

  {refined && (
    <div className="result-card">
      <h3>Refined Result:</h3>
      <p><strong>Species:</strong> {refined.bird}</p>
      <p><strong>Why:</strong> {refined.why}</p>
      <p><strong>Confidence:</strong> {refined.confidence}</p>
      <button
        onClick={() =>
          saveToJournal({
            name: refined.bird,
            description,
            location,
            why: refined.why,
            confidence: refined.confidence,
            date: new Date().toISOString(),
          })
        }
      >
        Save Refined Result to Journal
      </button>
    </div>
  )}

  {result && (
    <div className="result-card">
      <h3>Result:</h3>
      <p><strong>Species:</strong> {result.bird}</p>
      <p><strong>Why:</strong> {result.why}</p>
      <p><strong>Confidence:</strong> {result.confidence}</p>
      <button
        onClick={() =>
          saveToJournal({
            name: result.bird,
            description,
            location,
            why: result.why,
            confidence: result.confidence,
            date: new Date().toISOString(),
          })
        }
      >
        Save to Journal
      </button>
    </div>
  )}

  {followUp && (
    <div className="refine-box">
      <button onClick={() => setShowRefineForm(!showRefineForm)}>
        {showRefineForm ? "Back" : "Refine Your Result"}
      </button>

      {showRefineForm && (
        <form onSubmit={handleRefine}>
          <h3>Refine Your Result</h3>
          {followUp.map((q, i) => (
            <div key={i}>
              <label>{q}</label>
              <input
                type="text"
                required
                value={answers[i] || ""}
                onChange={(e) => {
                  const updated = [...answers];
                  updated[i] = e.target.value;
                  setAnswers(updated);
                }}
              />
            </div>
          ))}
          <button type="submit">Refine</button>
        </form>
        
      )}
      
    </div>
  )}

  <footer style={{ textAlign: "center", marginTop: "40px", fontSize: "14px", color: "#444" }}>
  Made with 🦅 and 🌿 by Louise
</footer>

</div>

  );
}

export default BirdIdentifier;
